import React from 'react';
import { Form, Select, Radio } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import {
  FormSection,
  StyledInput,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  FieldHint,
  AddDomainFormWrapper,
  DomainFormAlert,
} from '../Styles';
import { PLATFORMS, PLATFORM_SELECT_OPTIONS, isValidDomain, normalizeDomain } from '../constants';
import CopyableCode from './CopyableCode';
import type { AddDomainFormProps, DomainFormValues } from '../types';

const AddDomainForm: React.FC<AddDomainFormProps> = ({ loading, onSave, onCancel }) => {
  const { handleSubmit, control, watch } = useForm<DomainFormValues>({
    defaultValues: {
      domain: '',
      platform: 'vercel',
      customRecordType: 'CNAME',
      customDnsTarget: '',
    },
  });

  const platform = watch('platform');
  const customRecordType = watch('customRecordType');
  const customDnsTarget = watch('customDnsTarget');
  const isCustom = platform === 'custom';
  const cfg = PLATFORMS[platform];
  const previewTarget = isCustom ? customDnsTarget : cfg.dnsHint;
  const previewRecordType = isCustom ? customRecordType : (cfg.recordType || 'CNAME');

  const onSubmit = (data: DomainFormValues) => {
    onSave({
      domain: normalizeDomain(data.domain),
      platform: data.platform,
      recordType: previewRecordType as 'CNAME' | 'A',
      dnsTarget: isCustom ? data.customDnsTarget.trim() : cfg.dnsHint,
    });
  };

  return (
    <AddDomainFormWrapper>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <FormSection>

          <Controller
            name="domain"
            control={control}
            rules={{
              required: 'Please enter a domain name.',
              validate: (v) => isValidDomain(normalizeDomain(v)) || 'Please enter a valid domain (e.g. myrestaurant.com).',
            }}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Domain"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message ?? <FieldHint>Enter without http:// or https://</FieldHint>}
              >
                <StyledInput {...field} placeholder="e.g. myrestaurant.com or order.myrestaurant.com" />
              </Form.Item>
            )}
          />

          <Controller
            name="platform"
            control={control}
            render={({ field }) => (
              <Form.Item label="Hosting Platform" help={<FieldHint>Select where your website is deployed</FieldHint>}>
                <Select {...field} style={{ width: '100%' }} options={PLATFORM_SELECT_OPTIONS} />
              </Form.Item>
            )}
          />

          {isCustom && (
            <>
              <Controller
                name="customRecordType"
                control={control}
                render={({ field }) => (
                  <Form.Item label="DNS Record Type">
                    <Radio.Group {...field}>
                      <Radio value="CNAME">CNAME</Radio>
                      <Radio value="A">A Record</Radio>
                    </Radio.Group>
                  </Form.Item>
                )}
              />
              <Controller
                name="customDnsTarget"
                control={control}
                rules={{ required: 'Please enter the DNS target for your custom platform.' }}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="DNS Target / IP"
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message ?? <FieldHint>The value your domain provider should point to</FieldHint>}
                  >
                    <StyledInput
                      {...field}
                      placeholder={customRecordType === 'A' ? 'e.g. 76.76.21.21' : 'e.g. your-app.example.com'}
                    />
                  </Form.Item>
                )}
              />
            </>
          )}

          {previewTarget && (
            <DomainFormAlert
              type="info"
              showIcon
              description={
                <>
                  <div style={{ marginBottom: 6 }}>
                    After saving, add this <strong>{previewRecordType}</strong> record in your domain provider:
                  </div>
                  <CopyableCode value={previewTarget} />
                </>
              }
            />
          )}

          <ActionButtons>
            <SaveChangesButton icon={<CheckOutlined />} htmlType="submit" loading={loading}>
              Add Domain
            </SaveChangesButton>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
          </ActionButtons>

        </FormSection>
      </Form>
    </AddDomainFormWrapper>
  );
};

export default AddDomainForm;
